import { useNavigate } from 'react-router-dom';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import CardHeader from '@/shared-components/cards/components/CardHeader';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';
import { buildPublicContentPath } from '@/utils/contentRoutes';
import { resolveFollowState } from '@/utils/followState';

import './styles/CommunityArticleCard.css';

export default function CommunityArticleCard({ article }) {
  const navigate = useNavigate();
  const { cacheHandler } = useStateHandler();

  if (!article) return null;

  const articleId = article?._id || article?.id || article?.article_id;
  const coverImageId =
    article.coverImageId ||
    article.cover_image_id ||
    article.cover_image?.image_id;
  const coverUrl = coverImageId
    ? `${config.app.imageServiceDomain}/${coverImageId}/w=600`
    : null;

  const creator = article.creator || article.article_creator || {};
  const creatorId = creator?._id || article.creator_id || article.creatorId;
  const isFollowing = resolveFollowState({
    cacheHandler,
    userId: creatorId,
    imFollowing: creator?.im_following ?? article.creator_im_following ?? article.im_following,
  });

  const creatorUsername =
    article.creator_username || creator.user_username || creator.username;
  const creatorImage =
    article.creator_image ||
    creator.user_image?.image_id ||
    creator.image_id;
  const creatorType =
    article.creator_type || creator.user_type || creator.type;

  const timeAgo = article.createdAt
    ? Utils.elapsedTime(new Date(article.createdAt).getTime())
    : null;

  const handleUserClick = e => {
    e.stopPropagation();
    if (creatorUsername) navigate(`/user/${creatorUsername}`);
  };

  const handleCardClick = () => {
    if (articleId) navigate(buildPublicContentPath({ ...article, post_type: 'article' }, 'article'));
  };

  return (
    <div
      className={`community-article-card__container ${isFollowing ? 'following' : ''}`}
      onClick={handleCardClick}
    >
      <CardHeader
        image={creatorImage}
        profileType={creatorType}
        username={creatorUsername}
        subtitle="added a new article"
        timeAgo={timeAgo}
        onUserClick={handleUserClick}
      />

      {coverUrl ? (
        <img
          src={coverUrl}
          alt="Article cover"
          className="community-article-card__cover"
        />
      ) : (
        <div className="community-article-card__cover community-article-card__cover--placeholder" />
      )}

      <div className="community-article-card__content">
        <Text className="community-article-card__title">{article.title}</Text>
        {article.description && (
          <Text className="community-article-card__description" paragraph>
            {article.description}
          </Text>
        )}
      </div>
    </div>
  );
}
